import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import ProfileMenuSidebar, {
    IDropDownOptions,
    IProfileMenuSidebarPropsTexts,
} from "@tiendeo/react-components/dist/components/ProfileMenuSidebar/ProfileMenuSidebar";
import { useRouter } from "next/router";

import useTranslation from "translations/useTranslation";
import { useServices } from "contexts/ServiceContext";
import useProfileQuery from "queries/useProfileQuery";
import useCloseProfileMenu from "hooks/useCloseProfileMenu";
import useIsUserLogged from "hooks/useIsUserLogged";
import useLogout from "hooks/useLogout";
import { Analytics } from "services/Analytics/Analytics";
import { EventEnum } from "enums/EventEnum";
import useProfileCompletion from "hooks/useProfileCompletion";

const StyledProfileMenuContainer = styled.div<{ isVisible: boolean }>`
    margin-top: 56px;
    @media (min-width: ${(props) => props.theme.layout.breakpoints.xs}) {
        margin-top: 48px;
    }

    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: ${(props) => (props.isVisible ? "auto" : "none")};
    z-index: 10;
`;

const ProfileMenuSidebarContainer = () => {
    const router = useRouter();
    const services = useServices();
    const t = useTranslation();
    const configs = services.Configs;
    const isLoyaltyCardsActive = configs.loyalty_cards_active;
    const isAchievementsEnabled = configs.enable_achievements;

    const isLogged = useIsUserLogged();

    const isProfileMenuOpen = useMemo(
        () => services.UrlsService.isProfileMenu(router.asPath),
        [router.asPath, services.UrlsService],
    );

    const closeProfileMenu = useCloseProfileMenu();

    const login = useCallback(() => {
        if (services.WebviewService.IsWebView()) {
            services.WebviewService.Login();
            return;
        }
        services.NavigationService.Login();
    }, [services.NavigationService, services.WebviewService]);

    const onUserNameClick = useCallback(() => {
        services.NavigationService.Landing();
    }, [services.NavigationService]);

    const logout = useLogout();

    if (!configs.enable_login && isLogged) logout();

    const { data: { name: userProfileName, picture } = {} } = useProfileQuery({
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
    });

    const { max, value } = useProfileCompletion();

    const progressText = useMemo(
        () =>
            t("PROFILE_STEPPER_LABEL", {
                progressValue: value,
                maxValue: max,
            }),
        [t, max, value],
    );

    const Texts: IProfileMenuSidebarPropsTexts = useMemo(
        () => ({
            LABEL_LOGOUT: t("LOGOUT"),
            LOGIN_TEXT: t("ANALYTICS_LOGIN"),
            USER_NAME: userProfileName,
            PROFILE_STEPS_COMPLETION: progressText,
        }),
        [progressText, t, userProfileName],
    );

    const goToMyTiendeo = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.CategoryMyTiendeo);
        services.NavigationService.MyTiendeo();
    }, [services.NavigationService]);

    const goToFidelityCards = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.LoyaltyCards);
        services.NavigationService.LoyaltyCards();
    }, [services.NavigationService]);

    const goToMyMedals = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.MyMedals);
        services.NavigationService.MyMedals();
    }, [services.NavigationService]);

    const goToNotifications = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.Notifications);
        services.NavigationService.Notifications();
    }, [services.NavigationService]);

    const goToSavedPages = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.SavedPages);
        services.NavigationService.SavedPages();
    }, [services.NavigationService]);

    const goToHelp = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.CategoryHelp);
        services.NavigationService.Help();
    }, [services.NavigationService]);

    const menuOptions: IDropDownOptions[] = useMemo((): IDropDownOptions[] => {
        const myTiendeoContents: IDropDownOptions["contents"] = [];

        if (!configs.disable_favorites) {
            myTiendeoContents.push({
                level: 1,
                text: t("NOTIFICATIONS"),
                onClick: goToNotifications,
            });
        }
        myTiendeoContents.push({
            level: 1,
            text: t("YOUR_SAVED_OFFERS"),
            onClick: goToSavedPages,
        });
        if (isLoyaltyCardsActive && configs.enable_login) {
            myTiendeoContents.push({
                level: 1,
                text: t("LOYALTY_CARDS"),
                onClick: goToFidelityCards,
            });
        }
        if (isAchievementsEnabled && isLogged) {
            myTiendeoContents.push({
                level: 1,
                text: t("MY_MEDALS"),
                onClick: goToMyMedals,
            });
        }

        return [
            {
                level: 0,
                text: t("WEB_NAME"),
                isVisible: true,
                url: configs.url_tiendeo,
                isLanding: true,
            },
            {
                level: 0,
                text: t("MY_TIENDEO"),
                onClick: goToMyTiendeo,
                isVisible: true,
                realPath: services.UrlsService.PathMyTiendeo,
                contents: myTiendeoContents,
            },
            {
                level: 0,
                text: t("HELP"),
                realPath: services.UrlsService.PathHelp,
                isVisible: true,
                onClick: goToHelp,
            },
        ];
    }, [
        t,
        goToNotifications,
        goToSavedPages,
        isLoyaltyCardsActive,
        configs.enable_login,
        configs.url_tiendeo,
        isAchievementsEnabled,
        isLogged,
        goToMyTiendeo,
        services.UrlsService.PathMyTiendeo,
        services.UrlsService.PathHelp,
        goToHelp,
        goToFidelityCards,
        goToMyMedals,
    ]);

    return (
        <StyledProfileMenuContainer isVisible={isProfileMenuOpen}>
            <ProfileMenuSidebar
                menuOptions={menuOptions}
                login={configs.enable_login ? login : null}
                logout={configs.enable_login ? logout : null}
                Texts={Texts}
                isLogged={isLogged}
                onClose={closeProfileMenu}
                router={router}
                onUserNameClick={onUserNameClick}
                isVisible={isProfileMenuOpen}
                profileImgUrl={picture}
                activeGroup="profile"
                activeHeader
            />
        </StyledProfileMenuContainer>
    );
};

export default ProfileMenuSidebarContainer;
